// Inventario e insumos (7.3g): compras, existencias y el catálogo de insumos.
import type { SupabaseClient } from '@supabase/supabase-js';
import { guardarParametro } from './ajustes';
import { cargarInsumos, type Insumo } from './catalogo';
import { todas } from './pedidos';
import { cuenta, type Estado } from '../logica/pedidos';
import { siguienteCodigoInsumo } from '../logica/inventario';

const ok = ({ error }: { error: { message: string } | null }) => { if (error) throw new Error(/duplicate key|unique/i.test(error.message) ? 'Ya existe un insumo con ese nombre.' : error.message); };

export interface CompraInsumo {
  id: number; fecha: string; proveedor: string | null; codigo: string; cantidad: number; valor_antes_iva: number; pct_iva: number; iva_descontable: boolean; valor_costeo: number; nota: string | null;
}
export interface DatosInventario {
  insumos: Insumo[]; compras: CompraInsumo[]; formula: { sku: string; codigo: string; cantidad: number | null }[]; lineas: { sku: string; cantidad: number }[];
  ivaDescontable: boolean; proveedores: string[]; usos: Map<string, number>;
}

export async function cargarInventario(sb: SupabaseClient): Promise<DatosInventario> {
  const [insumos, com, form, prov, par, ped, lin] = await Promise.all([
    cargarInsumos(sb),
    todas<Record<string, unknown>>((a, b) => sb.from('compras').select('id, fecha, codigo, cantidad, valor_antes_iva, pct_iva, iva_descontable, valor_costeo, nota, proveedor:proveedores(nombre)').order('fecha').order('id').range(a, b)),
    todas<{ sku: string; codigo: string; cantidad: number | null }>((a, b) => sb.from('insumos_producto').select('sku, codigo, cantidad').order('sku').range(a, b)),
    sb.from('proveedores').select('nombre').order('nombre'),
    sb.from('parametros').select('valor').eq('clave', 'iva_descontable').maybeSingle(),
    todas<{ id: string; estado: Estado }>((a, b) => sb.from('pedidos').select('id, estado').order('id').range(a, b)),
    todas<{ pedido_id: string; sku: string; cantidad: number }>((a, b) => sb.from('pedido_lineas').select('pedido_id, sku, cantidad').order('id').range(a, b)),
  ]);
  const error = prov.error || par.error;
  if (error) throw new Error(error.message);
  const venden = new Set(ped.filter(cuenta).map(p => p.id));
  const compras = com.map(c => ({
    id: c.id as number, fecha: c.fecha as string, codigo: c.codigo as string, cantidad: +(c.cantidad as number), valor_antes_iva: +(c.valor_antes_iva as number), pct_iva: +(c.pct_iva as number),
    iva_descontable: !!c.iva_descontable, valor_costeo: +(c.valor_costeo as number), nota: (c.nota as string) || null, proveedor: ((c.proveedor as { nombre: string } | null)?.nombre) || null,
  }));
  const usos = new Map<string, number>();
  for (const x of [...form, ...compras]) usos.set(x.codigo, (usos.get(x.codigo) || 0) + 1);
  return {
    insumos, compras, formula: form.map(r => ({ ...r, cantidad: r.cantidad == null ? null : +r.cantidad })), usos,
    lineas: lin.filter(l => venden.has(l.pedido_id)).map(l => ({ sku: l.sku, cantidad: +l.cantidad })),
    ivaDescontable: !!(par.data?.valor as { activo?: boolean } | null)?.activo, proveedores: (prov.data || []).map(p => p.nombre as string),
  };
}

// El proveedor se escribe libre: si no existe, se crea.
async function proveedorId(sb: SupabaseClient, nombre: string): Promise<number | null> {
  const n = nombre.replace(/\s+/g, ' ').trim();
  if (!n) return null;
  const { data, error } = await sb.from('proveedores').select('id').ilike('nombre', n.replace(/[%_\\]/g, '\\$&')).maybeSingle();
  if (error) throw new Error(error.message);
  if (data) return data.id as number;
  const r = await sb.from('proveedores').insert({ nombre: n }).select('id').single();
  if (r.error) throw new Error(r.error.message);
  return r.data.id as number;
}
export async function registrarCompra(sb: SupabaseClient, c: { fecha: string; proveedor: string; codigo: string; cantidad: number; valor_antes_iva: number; pct_iva: number; iva_descontable: boolean }) {
  const { proveedor, ...fila } = c;
  ok(await sb.from('compras').insert({ ...fila, proveedor_id: await proveedorId(sb, proveedor) }));
}
export async function borrarCompra(sb: SupabaseClient, id: number) { ok(await sb.from('compras').delete().eq('id', id)); }

// IVA descontable para todo el negocio: cambia cómo entra al costo el IVA de todas las compras, también las ya registradas.
export async function cambiarIvaDescontable(sb: SupabaseClient, activo: boolean) {
  await guardarParametro(sb, 'iva_descontable', { activo });
  ok(await sb.from('compras').update({ iva_descontable: activo }).neq('iva_descontable', activo));
}

export type DatosInsumo = Pick<Insumo, 'nombre' | 'tipo' | 'unidad' | 'stock_minimo' | 'costo_estandar' | 'categoria_id' | 'notas'>;
export async function crearInsumo(sb: SupabaseClient, d: DatosInsumo, codigos: string[]): Promise<string> {
  const codigo = siguienteCodigoInsumo(codigos, d.tipo);
  ok(await sb.from('materiales').insert({ ...d, codigo }));
  return codigo;
}
export async function cambiarInsumoDatos(sb: SupabaseClient, codigo: string, d: Omit<DatosInsumo, 'tipo'>) { ok(await sb.from('materiales').update(d).eq('codigo', codigo)); }
export async function borrarInsumo(sb: SupabaseClient, codigo: string) { ok(await sb.from('materiales').delete().eq('codigo', codigo)); }
